import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon, CloudUpload, Image as ImageIcon, VideoLibrary, MusicNote, Description } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../../translate/i18n";
import api from "../../../services/api";
import { toast } from "react-toastify";
import CampaignMessageComposer from "../../CampaignMessageComposer";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 400,
    padding: theme.spacing(2),
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
}));

const NodeConfigModal = ({ open, onClose, node, onSave }) => {
  const classes = useStyles();
  const [nodeData, setNodeData] = useState({});
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [queues, setQueues] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (node) {
      setNodeData(node.data || {});
    } else {
      setNodeData({});
    }
  }, [node]);

  // ✅ Cargar tags cuando se abre el modal para nodos tag/removeTag
  useEffect(() => {
    if (open && (node?.type === "tag" || node?.type === "removeTag")) {
      loadTags();
    }
  }, [open, node]);

  // ✅ Cargar prompts (Agentes IA) cuando se abre el modal para nodo openai
  useEffect(() => {
    if (open && node?.type === "openai") {
      loadPrompts();
    }
  }, [open, node]);

  // ✅ Cargar departamentos (queues) cuando se abre el modal para nodo transferQueue
  useEffect(() => {
    if (open && node?.type === "transferQueue") {
      loadQueues();
    }
  }, [open, node]);

  const loadTags = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingTags(true);
      const { data } = await api.get("/tags");
      
      // ✅ Asegurar que data sea un array
      const tagsArray = Array.isArray(data) ? data : (Array.isArray(data?.tags) ? data.tags : []);
      
      if (isMountedRef.current) {
        setTags(tagsArray);
      }
    } catch (error) {
      console.error("Error cargando tags:", error);
      if (isMountedRef.current) {
        setTags([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingTags(false);
      }
    }
  };

  const loadPrompts = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingPrompts(true);
      const { data } = await api.get("/prompt");
      
      // ✅ Asegurar que data.prompts sea un array
      const promptsArray = Array.isArray(data?.prompts) ? data.prompts : [];
      
      if (isMountedRef.current) {
        setPrompts(promptsArray);
      }
    } catch (error) {
      console.error("Error cargando prompts:", error);
      if (isMountedRef.current) {
        setPrompts([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingPrompts(false);
      }
    }
  };

  const loadQueues = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingQueues(true);
      const { data } = await api.get("/queue");
      const queuesArray = Array.isArray(data) ? data : (Array.isArray(data?.queues) ? data.queues : []);
      if (isMountedRef.current) {
        setQueues(queuesArray);
      }
    } catch (error) {
      console.error("Error cargando departamentos:", error);
      if (isMountedRef.current) {
        setQueues([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingQueues(false);
      }
    }
  };

  if (!node || !open) return null;

  // ✅ Obtener nombre del nodo para el título del modal
  const getNodeTypeName = () => {
    const nodeTypeNames = {
      'start': 'Start',
      'text': 'Texto',
      'message': 'Texto',
      'image': 'Imagen',
      'video': 'Video',
      'audio': 'Audio',
      'document': 'Documento',
      'input': 'Input',
      'menu': 'Menú',
      'condition': 'Condición',
      'tag': 'Agregar Tag',
      'removeTag': 'Remover Tag',
      'openai': 'Agente IA',
      'httpRequest': 'HTTP Request',
      'wait': 'Wait',
      'typing': 'Typing',
      'setVariable': 'Set Variable',
      'transferQueue': 'Transferir a departamento',
    };
    return nodeTypeNames[node.type] || node.label || node.type || 'Nodo';
  };

  const handleSave = () => {
    if (uploadingMedia) {
      toast.error("Espera a que termine la subida del archivo");
      return;
    }

    const nodeType = node?.type;
    let nextData = { ...nodeData };

    if (nodeType === "menu") {
      const options = (nextData.options || []).filter((option) => String(option.text || "").trim());
      if (!String(nextData.message || "").trim()) {
        toast.error("Escribe el mensaje del menú");
        return;
      }
      if (options.length === 0) {
        toast.error("El menú necesita al menos una opción con texto");
        return;
      }
      nextData = { ...nextData, options };
    }
    if (["image", "video", "audio", "document"].includes(nodeType) && !nextData.mediaUrl) {
      toast.error("Sube un archivo antes de guardar este nodo");
      return;
    }
    if ((nodeType === "text" || nodeType === "message") && !String(nextData.text || "").trim()) {
      toast.error("Escribe el mensaje de texto del nodo");
      return;
    }
    if (nodeType === "input" && !String(nextData.message || "").trim()) {
      toast.error("Escribe el mensaje del input");
      return;
    }
    if (nodeType === "condition" && !String(nextData.variable || "").trim()) {
      toast.error("Indica la variable de la condición");
      return;
    }
    if ((nodeType === "tag" || nodeType === "removeTag") && !nextData.tagId) {
      toast.error("Selecciona un tag");
      return;
    }
    if (nodeType === "openai" && !nextData.promptId) {
      toast.error("Selecciona un Agente IA");
      return;
    }
    if (nodeType === "httpRequest" && !String(nextData.url || "").trim()) {
      toast.error("Indica la URL de la petición HTTP");
      return;
    }
    if (nodeType === "transferQueue" && !nextData.queueId) {
      toast.error("Selecciona un departamento");
      return;
    }
    if (nodeType === "setVariable" && !String(nextData.key || nextData.variable || "").trim()) {
      toast.error("Indica el nombre de la variable");
      return;
    }

    onSave({
      ...node,
      data: nextData,
    });
    onClose();
  };

  // ✅ Función para subir archivo multimedia
  const handleMediaUpload = async (file, mediaType) => {
    if (!file) return;

    try {
      setUploadingMedia(true);
      const formData = new FormData();
      formData.append("media", file);

      const { data } = await api.post("/flowbuilder/upload-media", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (data.success) {
        setNodeData({
          ...nodeData,
          mediaUrl: data.mediaUrl,
          fileName: data.fileName,
        });
        toast.success("Archivo subido exitosamente");
      } else {
        toast.error(data.error || data.message || "Error al subir archivo");
      }
    } catch (error) {
      console.error("Error subiendo archivo:", error);
      toast.error(error.response?.data?.error || "Error al subir archivo");
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleFileSelect = (e, mediaType) => {
    const file = e.target.files[0];
    if (file) {
      handleMediaUpload(file, mediaType);
    }
    // Reset input para permitir seleccionar el mismo archivo nuevamente
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderTextConfig = () => (
    <Box className={classes.tabPanel}>
      <CampaignMessageComposer
        label="Mensaje de texto"
        value={nodeData.text || ""}
        onChange={(next) => setNodeData({ ...nodeData, text: next })}
        placeholder="Escribe tu mensaje aquí. Puedes usar variables: {{name}}, {{number}}, {{email}}"
        rows={4}
        maxLength={4000}
        showCounter
      />
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
        Variables: {"{{name}}"}, {"{{number}}"}, {"{{email}}"}, {"{{appointmentDate}}"}, {"{{confirmationCode}}"}, {"{{serviceName}}"}, {"{{professionalName}}"}
      </Typography>
    </Box>
  );

  // ✅ Retrocompatibilidad: 'message' usa la misma configuración que 'text'
  const renderMessageConfig = renderTextConfig;

  const renderMediaConfig = (mediaType, label, icon, acceptedTypes) => {
    const hasMedia = !!nodeData.mediaUrl;
    const fileSizeLimit = mediaType === "image" ? "5MB" : mediaType === "video" || mediaType === "audio" ? "16MB" : "100MB";

    return (
      <Box className={classes.tabPanel}>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e, mediaType)}
        />
        
        {uploadingMedia && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Subiendo archivo...
            </Typography>
          </Box>
        )}

        {hasMedia ? (
          <Box mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                {icon}
                <Typography variant="body2" fontWeight="bold">
                  Archivo subido
                </Typography>
              </Box>
              <Chip
                label={nodeData.fileName || "Archivo"}
                size="small"
                color="primary"
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<CloudUpload />}
            >
              Cambiar archivo
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<CloudUpload />}
            disabled={uploadingMedia}
            style={{ marginBottom: 16, padding: 12 }}
          >
            Subir {label}
          </Button>
        )}

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Caption (opcional)"
          value={nodeData.caption || ""}
          onChange={(e) => setNodeData({ ...nodeData, caption: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Texto descriptivo que aparecerá con el archivo. Puedes usar variables: {{name}}"
        />
        
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Tamaño máximo: {fileSizeLimit}. Variables: {"{{name}}"}, {"{{appointmentDate}}"}, {"{{confirmationCode}}"}
        </Typography>
      </Box>
    );
  };

  const renderImageConfig = () => renderMediaConfig(
    "image",
    "Imagen",
    <ImageIcon fontSize="small" />,
    "image/*"
  );

  const renderVideoConfig = () => renderMediaConfig(
    "video",
    "Video",
    <VideoLibrary fontSize="small" />,
    "video/*"
  );

  const renderAudioConfig = () => renderMediaConfig(
    "audio",
    "Audio",
    <MusicNote fontSize="small" />,
    "audio/*"
  );

  const renderDocumentConfig = () => {
    const hasMedia = !!nodeData.mediaUrl;
    return (
      <Box className={classes.tabPanel}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e, "document")}
        />
        
        {uploadingMedia && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Subiendo archivo...
            </Typography>
          </Box>
        )}

        {hasMedia ? (
          <Box mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Description fontSize="small" />
                <Typography variant="body2" fontWeight="bold">
                  Documento subido
                </Typography>
              </Box>
              <Chip
                label={nodeData.fileName || "Documento"}
                size="small"
                color="primary"
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<CloudUpload />}
            >
              Cambiar archivo
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<CloudUpload />}
            disabled={uploadingMedia}
            style={{ marginBottom: 16, padding: 12 }}
          >
            Subir Documento
          </Button>
        )}

        <TextField
          fullWidth
          label="Nombre del archivo (opcional)"
          value={nodeData.fileName || ""}
          onChange={(e) => setNodeData({ ...nodeData, fileName: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="documento.pdf"
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Caption (opcional)"
          value={nodeData.caption || ""}
          onChange={(e) => setNodeData({ ...nodeData, caption: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Texto descriptivo que aparecerá con el documento"
        />
        
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Tamaño máximo: 100MB. Variables: {"{{name}}"}, {"{{appointmentDate}}"}, {"{{confirmationCode}}"}
        </Typography>
      </Box>
    );
  };

  const renderConditionConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        label={i18n.t("flowbuilders.modals.condition.variable")}
        value={nodeData.variable || ""}
        onChange={(e) => setNodeData({ ...nodeData, variable: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="ej: name, hasAppointment, userName"
      />
      <TextField
        fullWidth
        select
        label={i18n.t("flowbuilders.modals.condition.operator")}
        value={nodeData.operator || "equals"}
        onChange={(e) => setNodeData({ ...nodeData, operator: e.target.value })}
        variant="outlined"
        margin="normal"
        SelectProps={{ native: true }}
      >
        <option value="equals">Igual a</option>
        <option value="contains">Contiene</option>
        <option value="greater">Mayor que</option>
        <option value="less">Menor que</option>
      </TextField>
      <TextField
        fullWidth
        label={i18n.t("flowbuilders.modals.condition.value")}
        value={nodeData.value || ""}
        onChange={(e) => setNodeData({ ...nodeData, value: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="Para hasAppointment: true o false"
      />
      <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
        Variables: name, number, email, hasAppointment (true/false si tiene cita)
      </Typography>
    </Box>
  );

  const renderStartConfig = () => (
    <Box className={classes.tabPanel}>
      <Typography variant="body2" color="textSecondary">
        {i18n.t("flowbuilders.modals.start.description")}
      </Typography>
    </Box>
  );

  const renderInputConfig = () => (
    <Box className={classes.tabPanel}>
      <CampaignMessageComposer
        label="Mensaje"
        value={nodeData.message || ""}
        onChange={(next) => setNodeData({ ...nodeData, message: next })}
        placeholder="Por favor, ingresa tu respuesta:"
        rows={3}
        maxLength={2000}
        showCounter
      />
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Tipo de Input</InputLabel>
        <Select
          value={nodeData.inputType || "text"}
          onChange={(e) => setNodeData({ ...nodeData, inputType: e.target.value })}
          label="Tipo de Input"
        >
          <MenuItem value="text">Texto</MenuItem>
          <MenuItem value="number">Número</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="phone">Teléfono</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="Placeholder (opcional)"
        value={nodeData.placeholder || ""}
        onChange={(e) => setNodeData({ ...nodeData, placeholder: e.target.value })}
        variant="outlined"
        margin="normal"
      />
    </Box>
  );

  const renderMenuConfig = () => {
    const options = nodeData.options || [];

    const addOption = () => {
      setNodeData({
        ...nodeData,
        options: [...options, { text: "", value: "" }],
      });
    };

    const removeOption = (index) => {
      const newOptions = options.filter((_, i) => i !== index);
      setNodeData({ ...nodeData, options: newOptions });
    };

    const updateOption = (index, field, value) => {
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], [field]: value };
      setNodeData({ ...nodeData, options: newOptions });
    };

    return (
      <Box className={classes.tabPanel}>
        <CampaignMessageComposer
          label="Mensaje del menú"
          value={nodeData.message || ""}
          onChange={(next) => setNodeData({ ...nodeData, message: next })}
          placeholder="Por favor, selecciona una opción:"
          rows={3}
          maxLength={2000}
          showCounter
        />
        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Opciones del menú</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addOption}
              variant="outlined"
            >
              Agregar
            </Button>
          </Box>
          {options.map((option, index) => (
            <Box
              key={index}
              display="flex"
              gap={1}
              alignItems="center"
              mb={1}
              p={1}
              border="1px solid #e0e0e0"
              borderRadius={1}
            >
              <Typography variant="body2" style={{ minWidth: 30 }}>
                {index + 1}.
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Texto"
                value={option.text || ""}
                onChange={(e) => updateOption(index, "text", e.target.value)}
                variant="outlined"
                placeholder="Opción"
              />
              <TextField
                fullWidth
                size="small"
                label="Valor"
                value={option.value || ""}
                onChange={(e) => updateOption(index, "value", e.target.value)}
                variant="outlined"
                placeholder="valor"
              />
              <IconButton
                size="small"
                onClick={() => removeOption(index)}
                color="secondary"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {options.length === 0 && (
            <Typography variant="body2" color="textSecondary" align="center" py={2}>
              No hay opciones. Haz clic en "Agregar" para crear una.
            </Typography>
          )}
        </Box>
        
        {/* ✅ NUEVO: Configuración de mensaje de error personalizado */}
        <Box mt={3} p={2} border="1px solid #e0e0e0" borderRadius={1} bgcolor="#f5f5f5">
          <FormControlLabel
            control={
              <Checkbox
                checked={nodeData.enableCustomError || false}
                onChange={(e) => {
                  const isEnabled = e.target.checked;
                  // ✅ Si se habilita y no hay mensaje personalizado, establecer mensaje por defecto
                  if (isEnabled && !nodeData.customErrorMessage) {
                    setNodeData({
                      ...nodeData,
                      enableCustomError: true,
                      customErrorMessage: "⚠️ Opción no válida. Tienes {optionsCount} opciones disponibles:\n\n{optionsList}\n\nPor favor, selecciona una opción válida."
                    });
                  } else {
                    setNodeData({ ...nodeData, enableCustomError: isEnabled });
                  }
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Habilitar mensaje de error personalizado
              </Typography>
            }
          />
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginLeft: 32, marginTop: 4 }}>
            Si está deshabilitado, se usará el mensaje por defecto con las opciones disponibles
          </Typography>
          
          {nodeData.enableCustomError && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Mensaje de error personalizado"
              value={nodeData.customErrorMessage || "⚠️ Opción no válida. Tienes {optionsCount} opciones disponibles:\n\n{optionsList}\n\nPor favor, selecciona una opción válida."}
              onChange={(e) => setNodeData({ ...nodeData, customErrorMessage: e.target.value })}
              variant="outlined"
              margin="normal"
              helperText="Puedes usar {optionsCount} para el número de opciones y {optionsList} para la lista de opciones. Puedes modificar este mensaje o dejarlo tal cual."
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderTagConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Tag</InputLabel>
        <Select
          value={nodeData.tagId || ""}
          onChange={(e) => {
            const selectedTagId = e.target.value;
            const selectedTag = tags.find(tag => tag.id === selectedTagId);
            // ✅ Guardar tanto tagId como tagName para mostrar en el nodo visual
            setNodeData({ 
              ...nodeData, 
              tagId: selectedTagId,
              tagName: selectedTag ? selectedTag.name : ""
            });
          }}
          label="Tag"
          disabled={loadingTags}
        >
          {Array.isArray(tags) && tags.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingTags && (
        <Typography variant="body2" color="textSecondary">
          Cargando tags...
        </Typography>
      )}
    </Box>
  );

  const renderTransferQueueConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Departamento</InputLabel>
        <Select
          value={nodeData.queueId ?? ""}
          onChange={(e) => {
            const selectedQueueId = e.target.value;
            const selectedQueue = queues.find(q => q.id === selectedQueueId);
            setNodeData({
              ...nodeData,
              queueId: selectedQueueId,
              queueName: selectedQueue ? selectedQueue.name : ""
            });
          }}
          label="Departamento"
          disabled={loadingQueues}
        >
          {Array.isArray(queues) && queues.map((queue) => (
            <MenuItem key={queue.id} value={queue.id}>
              {queue.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingQueues && (
        <Typography variant="body2" color="textSecondary">
          Cargando departamentos...
        </Typography>
      )}
      <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
        El ticket se transferirá a este departamento (ej. Citas, Soporte).
      </Typography>
    </Box>
  );

  const renderRemoveTagConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Tag a Remover</InputLabel>
        <Select
          value={nodeData.tagId || ""}
          onChange={(e) => {
            const selectedTagId = e.target.value;
            const selectedTag = tags.find(tag => tag.id === selectedTagId);
            // ✅ Guardar tanto tagId como tagName para mostrar en el nodo visual
            setNodeData({ 
              ...nodeData, 
              tagId: selectedTagId,
              tagName: selectedTag ? selectedTag.name : ""
            });
          }}
          label="Tag a Remover"
          disabled={loadingTags}
        >
          {Array.isArray(tags) && tags.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingTags && (
        <Typography variant="body2" color="textSecondary">
          Cargando tags...
        </Typography>
      )}
    </Box>
  );

  const renderSetVariableConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        label="Nombre de Variable"
        value={nodeData.key || nodeData.variable || ""}
        onChange={(e) => setNodeData({ ...nodeData, key: e.target.value, variable: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="ej: nombreUsuario, respuesta, producto"
        helperText="Nombre de la variable (sin espacios, usar camelCase o snake_case)"
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Valor"
        value={nodeData.value ?? ""}
        onChange={(e) => setNodeData({ ...nodeData, value: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="Valor a guardar o {{variable}} para usar otras variables"
        helperText="Puedes usar variables del sistema: {{name}}, {{input}}, {{openaiResponse}}, etc."
      />
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        <strong>Ejemplos:</strong><br/>
        • Valor fijo: "Juan"<br/>
        • Usar variable del sistema: {"{{name}}"}<br/>
        • Usar respuesta de Input: {"{{input}}"}<br/>
        • Combinar: {"Hola {{name}}, tu respuesta fue: {{input}}"}
      </Typography>
    </Box>
  );

  const renderOpenAIConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Agente IA</InputLabel>
        <Select
          value={nodeData.promptId || ""}
          onChange={(e) => {
            const selectedPrompt = prompts.find(p => p.id === e.target.value);
            setNodeData({ 
              ...nodeData, 
              promptId: e.target.value,
              promptName: selectedPrompt?.name || ""
            });
          }}
          label="Agente IA"
          disabled={loadingPrompts}
        >
          {loadingPrompts ? (
            <MenuItem disabled>Cargando agentes...</MenuItem>
          ) : prompts.length === 0 ? (
            <MenuItem disabled>No hay agentes disponibles</MenuItem>
          ) : (
            prompts.map((prompt) => (
              <MenuItem key={prompt.id} value={prompt.id}>
                {prompt.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      {nodeData.promptId && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, marginBottom: 8 }}>
          ✅ Agente IA seleccionado: <strong>{nodeData.promptName || prompts.find(p => p.id === nodeData.promptId)?.name}</strong>
        </Typography>
      )}
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, padding: 8, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
        💡 <strong>Nota:</strong> Para crear un nuevo Agente IA, ve a la página <strong>"AGENTES IA"</strong> en el menú principal.
      </Typography>
    </Box>
  );

  const renderHTTPRequestConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        label="URL"
        value={nodeData.url || ""}
        onChange={(e) => setNodeData({ ...nodeData, url: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="https://api.example.com/endpoint"
      />
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Método HTTP</InputLabel>
        <Select
          value={nodeData.method || "GET"}
          onChange={(e) => setNodeData({ ...nodeData, method: e.target.value })}
          label="Método HTTP"
        >
          <MenuItem value="GET">GET</MenuItem>
          <MenuItem value="POST">POST</MenuItem>
          <MenuItem value="PUT">PUT</MenuItem>
          <MenuItem value="PATCH">PATCH</MenuItem>
          <MenuItem value="DELETE">DELETE</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Headers (JSON)"
        value={nodeData.headers ? JSON.stringify(nodeData.headers, null, 2) : ""}
        onChange={(e) => {
          try {
            const headers = e.target.value ? JSON.parse(e.target.value) : {};
            setNodeData({ ...nodeData, headers });
          } catch (error) {
            // Ignorar errores de parsing mientras se escribe
          }
        }}
        variant="outlined"
        margin="normal"
        placeholder='{"Content-Type": "application/json"}'
      />
      {(nodeData.method === "POST" || nodeData.method === "PUT" || nodeData.method === "PATCH") && (
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Body (JSON)"
          value={nodeData.body ? JSON.stringify(nodeData.body, null, 2) : ""}
          onChange={(e) => {
            try {
              const body = e.target.value ? JSON.parse(e.target.value) : {};
              setNodeData({ ...nodeData, body });
            } catch (error) {
              // Ignorar errores de parsing mientras se escribe
            }
          }}
          variant="outlined"
          margin="normal"
          placeholder='{"key": "value"}'
        />
      )}
    </Box>
  );

  const renderWaitConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        type="number"
        label="Delay (segundos)"
        value={nodeData.seconds ?? (nodeData.ms ? Math.round(Number(nodeData.ms) / 1000) : 1)}
        onChange={(e) => {
          const seconds = Math.max(0, Number(e.target.value) || 0);
          setNodeData({ ...nodeData, seconds, ms: seconds * 1000 });
        }}
        variant="outlined"
        margin="normal"
        inputProps={{ min: 0, max: 60, step: 1 }}
        helperText="Recomendado: 1–3s para evitar envíos instantáneos"
      />
    </Box>
  );

  const renderTypingConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        type="number"
        label="Typing (segundos)"
        value={nodeData.seconds ?? (nodeData.ms ? Math.round(Number(nodeData.ms) / 1000) : 1)}
        onChange={(e) => {
          const seconds = Math.max(0, Number(e.target.value) || 0);
          setNodeData({ ...nodeData, seconds, ms: seconds * 1000 });
        }}
        variant="outlined"
        margin="normal"
        inputProps={{ min: 0, max: 10, step: 1 }}
        helperText="Simula 'escribiendo...' antes de enviar el siguiente mensaje"
      />
    </Box>
  );

  const renderConfig = () => {
    switch (node.type) {
      // ✅ BUBBLES: Nodos de contenido (Arquitectura Typebot)
      case "text":
      case "message": // ✅ Retrocompatibilidad
        return renderTextConfig();
      case "image":
        return renderImageConfig();
      case "video":
        return renderVideoConfig();
      case "audio":
        return renderAudioConfig();
      case "document":
        return renderDocumentConfig();
      
      // ✅ INPUTS: Nodos de interacción
      case "input":
        return renderInputConfig();
      case "menu":
        return renderMenuConfig();
      
      // ✅ LOGIC: Nodos de lógica
      case "condition":
        return renderConditionConfig();
      case "tag":
        return renderTagConfig();
      case "removeTag":
        return renderRemoveTagConfig();
      case "openai":
        return renderOpenAIConfig();
      case "httpRequest":
        return renderHTTPRequestConfig();
      case "wait":
        return renderWaitConfig();
      case "typing":
        return renderTypingConfig();
      case "setVariable":
        return renderSetVariableConfig();
      case "transferQueue":
        return renderTransferQueueConfig();
      
      // ✅ CONTROL: Nodos de control
      case "start":
        return renderStartConfig();
      
      default:
        return (
          <Box className={classes.tabPanel}>
            <Typography variant="body2">
              {i18n.t("flowbuilders.modals.noConfig")}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configurar {getNodeTypeName()}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {renderConfig()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("flowbuilders.modals.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={uploadingMedia}>
          {i18n.t("flowbuilders.modals.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeConfigModal;
